import React, {useEffect, useState} from "react";
import {removeCompanies, updateCompany} from "../../../../api/company/CompanyService";
import {handleActiveError, handleActiveToast} from "../../../../components/sonner/ToastBasic";
import UpdateFile from "../../../../components/files/UpdateFile";
import UpdateImage from "../../../../components/files/UpdateImage";
import {DetailKvField, DetailKvTable, DetailPanel} from "../../../../components/detailLayout";
import {AttachmentContainer, AttachmentLane} from "../../../../components/files/AttachmentContainer";
import "../../../../components/detailLayout/detailListLayout.css";
import {useCacheRefresh} from "../../../../hooks/useCacheRefresh";
import {CacheType} from "../../../../constant/CodeSpecNum";
import {companyClassification, companyOperation} from "./CompanyCreateForm";

function CompanyAttachPanel({companyId, isEditing, attachViewKey}) {
    const tableInfo = {tableName: "company", joinKey: companyId};

    return (
        <AttachmentContainer>
            <AttachmentLane label="파일" variant="file">
                <UpdateFile
                    key={`company-file-${attachViewKey}`}
                    readOnly={!isEditing}
                    displayMode="list"
                    className="update-image"
                    tableInfo={tableInfo}
                />
            </AttachmentLane>
            <AttachmentLane label="이미지" variant="image">
                <UpdateImage
                    key={`company-image-${attachViewKey}`}
                    readOnly={!isEditing}
                    displayMode="list"
                    className="update-image"
                    tableInfo={tableInfo}
                />
            </AttachmentLane>
        </AttachmentContainer>
    );
}

function CompanyBasicInfoPanel({data, isEditing, fieldAuth, onChange}) {
    const inputProps = (field) => ({
        className: "basic-input input-style",
        value: data[field] ?? "",
        onChange: (e) => onChange(field, e.target.value),
    });

    return (
        <DetailPanel title="기본정보">
            <DetailKvTable>
                <tbody>
                    <tr>
                        <DetailKvField
                            isEditing={isEditing}
                            label="회사명칭"
                            value={data.name}
                            required
                            auth={fieldAuth}
                            edit={<input {...inputProps("name")} />}
                        />
                        <DetailKvField
                            isEditing={isEditing}
                            label="관리번호"
                            value={data.number}
                            auth={fieldAuth}
                            edit={<input {...inputProps("number")} />}
                        />
                    </tr>
                    <tr>
                        <DetailKvField
                            isEditing={isEditing}
                            label="사무실번호"
                            value={data.officePhone}
                            auth={fieldAuth}
                            edit={<input {...inputProps("officePhone")} />}
                        />
                        <DetailKvField
                            isEditing={isEditing}
                            label="대표연락망"
                            value={data.employeeContact}
                            auth={fieldAuth}
                            edit={<input {...inputProps("employeeContact")} />}
                        />
                    </tr>
                    <tr>
                        <DetailKvField
                            isEditing={isEditing}
                            label="팩스번호"
                            value={data.fax}
                            auth={fieldAuth}
                            edit={<input {...inputProps("fax")} />}
                        />
                        <DetailKvField
                            isEditing={isEditing}
                            label="비상연락망"
                            value={data.emergencyPhone}
                            auth={fieldAuth}
                            edit={<input {...inputProps("emergencyPhone")} />}
                        />
                    </tr>
                    <tr>
                        <DetailKvField
                            isEditing={isEditing}
                            label="홈페이지"
                            value={data.uri}
                            auth={fieldAuth}
                            edit={<input {...inputProps("uri")} />}
                        />
                        <DetailKvField
                            isEditing={isEditing}
                            label="이메일"
                            value={data.email}
                            auth={fieldAuth}
                            edit={<input {...inputProps("email")} />}
                        />
                    </tr>
                    <tr>
                        <DetailKvField
                            isEditing={isEditing}
                            label="주소"
                            value={data.address}
                            colspan={3}
                            auth={fieldAuth}
                            edit={<input {...inputProps("address")} />}
                        />
                    </tr>
                    <tr>
                        <DetailKvField
                            isEditing={isEditing}
                            label="비고"
                            value={data.etc}
                            colspan={3}
                            auth={fieldAuth}
                            edit={
                                <textarea
                                    className="basic-text input-style"
                                    value={data.etc ?? ""}
                                    onChange={(e) => onChange("etc", e.target.value)}
                                />
                            }
                        />
                    </tr>
                    <tr>
                        <DetailKvField
                            isEditing={isEditing}
                            label="운영여부"
                            value={data.operation}
                            auth={fieldAuth}
                            edit={
                                <select
                                    className="basic-input input-style"
                                    value={data.operation ?? ""}
                                    onChange={(e) => onChange("operation", e.target.value)}
                                >
                                    {companyOperation.map((option) => (
                                        <option key={option.id} value={option.id}>
                                            {option.name}
                                        </option>
                                    ))}
                                </select>
                            }
                        />
                        <DetailKvField
                            isEditing={isEditing}
                            label="업체구분"
                            value={data.classification}
                            auth={fieldAuth}
                            edit={
                                <select
                                    className="basic-input input-style"
                                    value={data.classification ?? ""}
                                    onChange={(e) => onChange("classification", e.target.value)}
                                >
                                    {companyClassification.map((option) => (
                                        <option key={option.id} value={option.id}>
                                            {option.name}
                                        </option>
                                    ))}
                                </select>
                            }
                        />
                    </tr>
                </tbody>
            </DetailKvTable>
        </DetailPanel>
    );
}

function CompanyManageEditor({company, setCompany, onDeleted}) {
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(company);
    const refreshCache = useCacheRefresh();
    const canUpdate = company?.auth?.update !== false;
    const fieldAuth = isEditing && canUpdate;

    useEffect(() => {
        setDraft(company);
        setIsEditing(false);
    }, [company?.id]);

    useEffect(() => {
        if (!isEditing) {
            setDraft(company);
        }
    }, [company, isEditing]);

    if (!company) {
        return null;
    }

    const handleChange = (field, value) => {
        setDraft((prev) => ({...prev, [field]: value}));
    };

    const handleSave = async () => {
        if (!draft.name?.trim()) {
            alert("필수값이 누락되었습니다.");
            return;
        }

        const response = await updateCompany({...draft, id: company.id});
        if (response?.status === 200) {
            refreshCache(CacheType.company);
            handleActiveToast({message: "수정되었습니다."});
            setCompany({...company, ...draft});
            setIsEditing(false);
        } else if (response?.status === 201 || response?.status === 202) {
            handleActiveError({message: response.data?.title, description: response.data?.reason});
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("선택한 업체를 삭제하시겠습니까?\n삭제 후 복원할 수 없습니다.")) {
            return;
        }

        const response = await removeCompanies({ids: [company.id]});
        if (response.status === 200) {
            refreshCache(CacheType.company);
            handleActiveToast({message: "데이터가 삭제되었습니다"});
            onDeleted?.(company);
        } else if (response.status === 201 || response.status === 202) {
            handleActiveError({message: response.data.title, description: response.data.reason});
        } else if (response.status === 403) {
            handleActiveError({message: "권한이 없습니다."});
        } else {
            handleActiveError({message: "알 수 없는 에러 발생."});
        }
    };

    const viewData = isEditing ? draft : company;
    const attachViewKey = company.updatedAt ?? company.id;

    return (
        <div className="company-manage-editor-form">
            <CompanyBasicInfoPanel
                data={viewData}
                isEditing={isEditing}
                fieldAuth={fieldAuth}
                onChange={handleChange}
            />

            <CompanyAttachPanel
                companyId={company.id}
                isEditing={isEditing}
                attachViewKey={attachViewKey}
            />

            <div className="code-editor__actions">
                {canUpdate && (
                    <button type="button" className="code-manage-delete-btn" onClick={handleDelete}>
                        삭제
                    </button>
                )}
                {!isEditing && canUpdate && (
                    <button type="button" className="save-btn" onClick={() => setIsEditing(true)}>
                        수정
                    </button>
                )}
                {isEditing && (
                    <>
                        <button
                            type="button"
                            className="code-manage-neutral-btn"
                            onClick={() => {
                                setDraft(company);
                                setIsEditing(false);
                            }}
                        >
                            취소
                        </button>
                        <button type="button" className="save-btn" onClick={handleSave}>
                            저장
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

export default CompanyManageEditor;
